import React, { type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CircleExclamation,
  Xmark,
} from "libs/design-system/src/assets/Icons";
import {
  FormatCurrencyForDisplay,
  PageStatus,
  SkeletonLoader,
  Badge,
} from "@unified-checkout/design-system";
import { formatToLocalizedDateTime } from "libs/design-system/src/utils/dateFormats";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { SYSTEM_EVENTS_GROUPING_CODES, SYSTEM_EVENTS_SRC_FEATURES } from "@app/dashboard/constants/tracking_events/system";
import { useLangStore } from "@app/dashboard/store/lang";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { InfoCard } from "@app/dashboard/components/inner_layout/Cards/InfoCard";
import { Editbox } from "@app/dashboard/views/pages/inner_layout/PaymentIntegrations/components/EditCard";
import { Header } from "./Header";
import { notify } from "@app/dashboard/utils/notifier";
import { editPaymentIntegration } from "@app/dashboard/services/inner_layout/payment_integrations/edit";
import { getIntegrations } from "@app/dashboard/services/inner_layout/integrations";
import { IEditCallbacksPayload } from "@app/dashboard/types/innerLayout/payment_integrations/edit-integrations";
import { onToasterShowHandler } from "@app/dashboard/utils/tracking/onToasterShowHandler";
import styles from "./single-integration.module.scss";

const EMPTY_VALUE = '-';
const MAX_NOTIFICATION_WIDTH = '400px';
const NOTIFICATION_TIMEOUT = 3;

export const SinglePaymentIntegration = () => {

  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const {
    isLoading,
    isError,
    data: integration,
    refetch,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_INTEGRATION_DETAILS, lang, id, isLive],
    queryFn: () => getIntegrations(500),
    refetchOnMount: "always",
    select: (response) => {
      return response?.data?.data?.results?.find((item: any) => String(item.id) === String(id));
    }
  });


  const getToasterConfig = ({ type, msg }: { type: 'success' | 'error'; msg: string }) => ({
    maxWidth: MAX_NOTIFICATION_WIDTH,
    timeout: NOTIFICATION_TIMEOUT,
    ...(type === 'error' && { icon: Xmark }),
    onShowHandler: () =>
      onToasterShowHandler({
        type,
        trackingCode: SYSTEM_EVENTS_GROUPING_CODES.PAYMENT_INTEGRATIONS.DETAILS.EDIT_INTEGRATION,
        msg,
        srcFeature: SYSTEM_EVENTS_SRC_FEATURES.PAYMENT_INTEGRATIONS.DETAILS,
      }),
  });

  const { mutate: editIntegration, isPending: isUpdating } = useMutation({
    mutationFn: (payload: IEditCallbacksPayload) =>
      editPaymentIntegration({ integrationId: id as string, payload }),
    onSuccess: () => {
      const successMsg = t(
        'innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.succesMassage'
      );
      notify.success(successMsg, getToasterConfig({ type: 'success', msg: successMsg }));
    },
    onError: (error: any) => {
      const errMsg = error.response.data?.integration_name?.[0] || t('errors.somethingWentWrong');
      notify.error(errMsg || t('errors.somethingWentWrong'), getToasterConfig({ type: 'error', msg: errMsg }));
    },
  });


  const singleIntegrationDetails = integration
  const {
    id: integrationId,
    currency,
    integration_type: channel,
    gateway_type: paymentMethod,
    is_shopify,
    created_at,
    integration_name,
    transaction_processed_callback,
    transaction_response_callback
  } = singleIntegrationDetails || {};

  const detailsBoxRenderer = (
    {
      box = undefined,
      height,
      noOfRows,
      key
    }: {
      box: ReactNode,
      height: string,
      noOfRows: number,
      key: number | string
    }
  ): ReactNode => {
    return (
      isLoading || isFetching ?
        <SkeletonLoader
          type="bordered_box"
          noOfRows={noOfRows}
          height={height}
          key={key}
        />
        :
        box && <React.Fragment key={key}>{box}</React.Fragment>
    )
  };

  const handleSave = (values: any) => {
    editIntegration({
      ...(values.integrationName !== integration_name && { integration_name: values.integrationName }),
      transaction_response_callback: values.redirectUrl,
      transaction_processed_callback: values.webhookUrl
    });
  };

  const allBoxes = [
    detailsBoxRenderer({
      box: (
        <Editbox
          integrationName={integration_name}
          webhookUrl={transaction_processed_callback}
          redirectUrl={transaction_response_callback}
          onSave={handleSave}
          isSaving={isUpdating}
        />
      ),
      noOfRows: 6,
      height: "20rem",
      key: "edit-box"
    }),

    detailsBoxRenderer({
      box: <InfoCard
        title={t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.title')}
        noOfColumns={1}
        pairsList={
          [
            {
              label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.transactionId'),
              value: integrationId || EMPTY_VALUE
            },
            {
              label: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.currency'),
              value: FormatCurrencyForDisplay(currency) || EMPTY_VALUE
            },
            {
              label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.channel'),
              value: channel || EMPTY_VALUE
            },
            {
              label: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.paymentMethod'),
              value: paymentMethod
                ?
                <Badge color="gray" text={paymentMethod} />
                :
                EMPTY_VALUE
            },
            {
              label: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.typeOfIntegration'),
              value: is_shopify
                ?
                t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.shopify')
                :
                t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.nonShopify')
            },
            {
              label: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.creationDate'),
              value: created_at
                ?
                formatToLocalizedDateTime(created_at, lang)
                :
                EMPTY_VALUE
            }
          ]}
      />,
      noOfRows: 6,
      height: "20rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.title')
    }),

  ];

  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0); // Odd-indexed items

  const content = <>
    {
      isMobile ?
        <>
          {col1Boxes}
          {col2Boxes}
        </>
        :
        [col1Boxes, col2Boxes].map((colContent, index) =>
          <div className={styles["single-integration__main__col"]} key={`single-transaction-col-${index}`}>
            {colContent}
          </div>
        )
    }
  </>;

  return (
    <div className={styles["single-integration"]}>
      {
        isError ?
          <PageStatus
            status="error"
            title={t('innerLayout.errors.smthWentWrong')}
            description={t('innerLayout.errors.dataRetrievalErr')}
            icon={CircleExclamation}
          />
          :
          <>
            <Header
              id={Number(id)}
              data={singleIntegrationDetails}
              isLoading={isLoading || isFetching}
              isError={isError}
              refetchData={refetch}
            />

            <div className={styles["single-integration__main"]}>
              {content}
            </div>
          </>
      }
    </div>
  )
}